/**
 * Accessible modal dialogs: focus moves in, is trapped while open, Escape closes, and focus returns to
 * whatever opened it. Content is plain markup, so forms inside can use the same data-act / data-submit hooks.
 */
import { html, toHtml } from '../core/html.js';
import { patch, $, $$ } from '../core/dom.js';
import { register } from '../core/events.js';
import { ic } from './components.js';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
let opener = null;
let onCloseCallback = null;
let resolveConfirm = null;
let installed = false;

function root() {
  return $('#modal-root');
}

function shell({ title, body, foot, wide }) {
  return html`<div class="mback" data-act="modal.backdrop"><div class="modal ${wide ? 'wide' : ''}" role="dialog" aria-modal="true" aria-labelledby="modal-title" tabindex="-1">
    <div class="mh"><h3 id="modal-title">${title}</h3><button type="button" class="btn icon" data-act="modal.close" aria-label="Close">${ic('x')}</button></div>
    <div class="mb" id="modal-body">${body}</div>${foot ? html`<div class="mf" id="modal-foot">${foot}</div>` : ''}</div></div>`;
}

export function isModalOpen() {
  return Boolean(root()?.firstElementChild);
}

export function openModal({ title, body, foot = '', wide = false, onClose = null }) {
  const host = root();
  if (!host) return;
  if (!isModalOpen()) {
    opener = document.activeElement;
    document.body.classList.add('modal-open');
  }
  onCloseCallback = onClose;
  patch(host, toHtml(shell({ title, body, foot, wide })));
  const dialog = $('.modal', host);
  const first = $('[autofocus], input:not([type=hidden]), textarea, select', dialog) || $('.mf .btn:last-child', dialog) || dialog;
  first.focus();
}

/** Replace the body (and footer) of the open modal without losing focus: used by multi-step dialogs. */
export function updateModal({ body, foot }) {
  const host = root();
  if (!isModalOpen()) return;
  patch($('#modal-body', host), toHtml(body));
  const footEl = $('#modal-foot', host);
  if (foot !== undefined) {
    if (footEl) patch(footEl, toHtml(foot));
    else if (foot) $('.modal', host).insertAdjacentHTML('beforeend', `<div class="mf" id="modal-foot">${toHtml(foot)}</div>`);
  }
}

export function closeModal(result = false) {
  const host = root();
  if (!host || !host.firstElementChild) return;
  host.replaceChildren();
  document.body.classList.remove('modal-open');
  const cb = onCloseCallback;
  onCloseCallback = null;
  if (resolveConfirm) {
    const done = resolveConfirm;
    resolveConfirm = null;
    done(result === true);
  }
  if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
  opener = null;
  if (cb) cb();
}

/** Ask a yes/no question. Resolves to true only if the person confirms. */
export function confirmBox({ title, text, label = 'Confirm', danger = false, cancel = 'Cancel' }) {
  return new Promise((resolve) => {
    if (resolveConfirm) resolveConfirm(false);
    resolveConfirm = resolve;
    openModal({
      title,
      body: html`<p>${text}</p>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">${cancel}</button><button type="button" class="btn ${danger ? 'danger' : ''}" data-act="modal.yes">${label}</button>`,
    });
  });
}

function trapTab(event) {
  if (event.key !== 'Tab' || !isModalOpen()) return;
  const items = $$(FOCUSABLE, $('.modal', root())).filter((el) => el.offsetParent !== null);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function installModal() {
  if (installed) return;
  installed = true;
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isModalOpen()) {
      event.stopPropagation();
      closeModal(false);
    }
    trapTab(event);
  }, true);
  register({
    'modal.close': () => closeModal(false),
    'modal.yes': () => closeModal(true),
    'modal.backdrop': (el, event) => {
      if (event.target === el) closeModal(false);
    },
  });
}
